import type { ThemeState } from '@atlaskit/tokens/theme-config';
import { themeObjectToString } from '@atlaskit/tokens/theme-object-to-string';

import { getSessionData } from '@confluence/session-data/entry-points/SessionData.ts';
import {
	API_DEFAULT_THEME_STATE,
	getEffectiveThemeState,
} from '@confluence/theming-utils/entry-points/getEffectiveTheme.ts';
import { getWorkspaceARI } from '@confluence/theming-utils/entry-points/getWorkspaceARI.ts';

import { ErrorSubjects, ErrorSubjectIds, reportThemingError } from './errorHandling.ts';
import type {
	WorkspaceThemePreferenceQuery as WorkspaceThemePreferenceQueryType,
	WorkspaceThemePreferenceQueryVariables,
} from './graphql/__types__/WorkspaceThemePreferenceQuery.ts';
import WorkspaceThemePreferenceQuery from './graphql/__generated__/WorkspaceThemePreferenceQuery.graphql.ts';
import WorkspaceThemePreferenceMutation from './graphql/__generated__/WorkspaceThemePreferenceMutation.graphql.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { mutateInterop } from '@confluence/apollo-relay-interop/entry-points/mutate.ts';
/**
 * Remote theme storage implementation using shared user preferences API.
 * This implementation is used when the 'shared-user-preferences' feature flag is enabled.
 * Uses Apollo client for GraphQL operations.
 */

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';

// TODO (DST): There is a Relay implementation of Shared User Preferences operations in @atlassian/shared-user-preferences-api/workspace-theme-management, however it doesn't work in confluence. Please replace with the methods once the Apollo-to-Relay migration is completed.
export class RemoteThemeStorageShared {
	public static async getThemeRemote(): Promise<Partial<ThemeState>> {
		const sessionData = await getSessionData();
		const { cloudId, activationId, userId } = sessionData;

		if (!userId) {
			return getEffectiveThemeState(API_DEFAULT_THEME_STATE);
		}

		const workspaceAri = getWorkspaceARI({ cloudId, activationId });

		const { data, error } = await queryInterop<
			WorkspaceThemePreferenceQueryType,
			WorkspaceThemePreferenceQueryVariables
		>({
			query: WorkspaceThemePreferenceQuery,
			interopApolloAggClient: true,
			variables: {
				workspaceAri,
				accountId: userId,
			},
		});

		if (error) {
			throw error;
		}

		const themeString = data?.settings_userPreferencesResolved?.theme;

		// Empty string means user hasn't set a preference, treat as default (Auto when shared-user-preferences is enabled).
		return getEffectiveThemeState(themeString || API_DEFAULT_THEME_STATE);
	}

	public static async saveThemeRemote(theme: Partial<ThemeState>): Promise<void> {
		try {
			const sessionData = await getSessionData();
			const { cloudId, activationId, userId } = sessionData;

			if (!userId) {
				return;
			}

			const workspaceAri = getWorkspaceARI({ cloudId, activationId });

			// Serialize the theme state to a string using Atlaskit's serializer
			const themeSerialized = themeObjectToString(theme);

			await mutateInterop({
				mutation: WorkspaceThemePreferenceMutation,
				variables: {
					workspaceAri,
					accountId: userId,
					theme: themeSerialized,
				},
			});
		} catch (e) {
			const attributes = {
				message: e.message,
				stack: e.stack,
				theme,
			};
			await reportThemingError(
				ErrorSubjects.remoteThemeStorage,
				ErrorSubjectIds.saveThemeRemote,
				attributes,
			);
		}
	}
}
